/*
 * Mirrors the subscription widget's chosen plan in the theme's product price.
 *
 * The Recharge widget owns the form's selling_plan field and gives the theme
 * no event to hang off, so the field is both listened to (for clicks) and
 * polled (for the values the widget sets in script).
 *
 * Both the plan price and the plain variant price are written from the data
 * below rather than from a cached copy of the markup: product-info.js updates
 * the price node in place on a variant change, so anything cached goes stale,
 * and anything injected gets wiped. Driving every state from data makes each
 * pass idempotent and lets a wiped price heal itself on the next one.
 */
(function () {
  const POLL_INTERVAL = 250;

  class SellingPlanPriceSync {
    constructor(dataScript) {
      this.sectionId = dataScript.dataset.sellingPlanPriceSync;
      this.variants = JSON.parse(dataScript.textContent);
      this.root = dataScript.closest('product-info') || document;

      this.sync = this.sync.bind(this);
      // Capture, so a radio inside the widget is seen however it is wrapped.
      document.addEventListener('change', this.sync, true);
      this.timer = setInterval(() => {
        if (this.root !== document && !document.contains(this.root)) {
          clearInterval(this.timer);
          document.removeEventListener('change', this.sync, true);
          return;
        }
        if (!document.hidden) this.sync();
      }, POLL_INTERVAL);

      this.sync();
    }

    get scope() {
      return this.root === document ? document : this.root;
    }

    currentVariant() {
      const field =
        this.scope.querySelector('form[action*="/cart/add"] [name="id"]') ||
        this.scope.querySelector('[name="id"]');
      return field && field.value ? this.variants[String(field.value)] : null;
    }

    currentPlan(variant) {
      if (!variant) return null;

      const fields = this.scope.querySelectorAll('[name="selling_plan"]');
      let id = '';

      fields.forEach((field) => {
        if (field.type === 'radio' || field.type === 'checkbox') {
          if (field.checked && field.value) id = field.value;
        } else if (field.value) {
          id = field.value;
        }
      });

      const plan = id ? variant.plans[String(id)] : null;
      // A plan that saves nothing leaves the theme's own price alone.
      return plan && plan.price < variant.price ? plan : null;
    }

    sync() {
      // The section renders a price block per layout, and both carry the same
      // id, so every match is painted rather than only the first.
      const prices = this.scope.querySelectorAll('[id="price-' + this.sectionId + '"] .price');
      if (!prices.length) return;

      const variant = this.currentVariant();
      if (!variant) return;

      const plan = this.currentPlan(variant);
      const onSale = variant.compare_at_price > variant.price;

      prices.forEach((price) => {
        if (plan) {
          // The one-time price becomes the struck-through "was", the way the
          // widget shows it.
          this.paint(price, true, variant.price_formatted, plan.price_formatted);
        } else if (onSale) {
          this.paint(price, true, variant.compare_at_price_formatted, variant.price_formatted);
        } else {
          this.paint(price, false, null, variant.price_formatted);
        }
      });
    }

    paint(price, showSale, wasText, nowText) {
      price.classList.toggle('price--on-sale', showSale);
      if (showSale) price.classList.remove('price--no-compare');

      const regular = price.querySelector('.price__regular .price-item--regular');
      if (regular) setText(regular, nowText);

      const sale = price.querySelector('.price__sale');
      if (!sale) return;

      const saleItem = sale.querySelector('.price-item--sale');
      if (saleItem) setText(saleItem, nowText);

      if (!showSale) return;

      // The theme omits the <s> on some products, so it is built rather than
      // assumed.
      let was = sale.querySelector('s.price-item--regular');
      if (!was) {
        const wrapper = document.createElement('span');
        was = document.createElement('s');
        was.className = 'price-item price-item--regular';
        wrapper.appendChild(was);
        sale.insertBefore(wrapper, sale.firstChild);
      }
      setText(was, wasText);
    }
  }

  function setText(element, text) {
    if (element.textContent.trim() !== text) element.textContent = text;
  }

  function init() {
    document.querySelectorAll('[data-selling-plan-price-sync]').forEach((script) => {
      if (script.dataset.sellingPlanPriceSyncReady) return;
      script.dataset.sellingPlanPriceSyncReady = 'true';
      try {
        new SellingPlanPriceSync(script);
      } catch (error) {
        console.error('selling-plan-price-sync:', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
